import { ApplicationRef, DestroyRef, Injectable, inject, signal } from '@angular/core';
import {SwUpdate, VersionReadyEvent} from '@angular/service-worker';
import {Logger} from '../utils/logger';
import {concat, interval} from 'rxjs';
import {filter, first} from 'rxjs/operators';

/** How often an open tab asks whether there is a new version, besides when it comes back into view. */
const CHECK_EVERY_MS = 30 * 60 * 1000;
/** The same without a service worker: it costs one small page, so it asks more often. */
const PAGE_CHECK_EVERY_MS = 5 * 60 * 1000;
/** The most a reload waits for unsaved work to go out. */
const SAVE_BEFORE_RELOAD_MS = 5000;

/**
 * A new release, and the app still open in a tab from before it.
 *
 * The app runs from its service worker's cache, so a tab left open keeps the
 * old code after a release, and the old code may no longer match the backend
 * or the database rules: after 8.0.0, customers still on 7.2.2 could not open
 * their projects, because a new version only offered a Reload in a snackbar.
 * Now a new version, once downloaded, blocks the app until it is reloaded
 * (owner, 2026-10-29: "the webapp should be blocked until the update"):
 * `required` says so and the shell shows the screen. A service worker that
 * can no longer serve this version (`unrecoverable`) blocks it the same way.
 *
 * Only production builds have the service worker. Without one (the dev
 * site's builds, a browser that has none) a tab compares the build it runs
 * with the one the site serves now, as the admin console does: every build
 * names its main script by its content (main-<hash>.js), and index.html,
 * which is never cached, names the current one. The development server's
 * plain main.js has no hash: nothing is asked there.
 *
 * What has not been saved yet (the map's queued writes, settings) registers
 * with `beforeReload` and goes out first, so reloading loses nothing.
 */
@Injectable({
  providedIn: 'root'
})
export class UpdateService {
  private updates = inject(SwUpdate);
  private pendingWork: (() => Promise<unknown>)[] = [];

  /** The main script this tab runs, when it is compared with the site's ('' when it is not). */
  private loadedMain = '';
  private pageChecking = false;

  /** A newer version is ready: the app is blocked until it is reloaded. */
  readonly required = signal(false);

  constructor() {
    const appRef = inject(ApplicationRef);
    const updates = this.updates;
    const stop = new AbortController();
    inject(DestroyRef).onDestroy(() => stop.abort());

    if (updates.isEnabled) {
      updates.versionUpdates.pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      ).subscribe(event => {
        Logger.log('current version is', event.currentVersion);
        Logger.log('available version is', event.latestVersion);
        updates.activateUpdate()
          .catch(err => Logger.error('Activating the new version failed', err))
          .finally(() => this.required.set(true));
      });
      updates.unrecoverable.subscribe(event => {
        Logger.error('The service worker cannot serve this version', event.reason);
        this.required.set(true);
      });

      // Allow the app to stabilize first, then ask every half hour, and
      // whenever the tab comes back into view (a tab left open over a release).
      const appIsStable$ = appRef.isStable.pipe(first(isStable => isStable === true));
      const asking = concat(appIsStable$, interval(CHECK_EVERY_MS)).subscribe(() => this.checkForUpdates());
      stop.signal.addEventListener('abort', () => asking.unsubscribe());
    } else {
      this.loadedMain = this.mainScriptOfThisTab();
      if (!this.loadedMain) {
        return;
      }
      // The page this tab loaded is the newest there is: ask from now on.
      const asking = setInterval(() => this.checkForUpdates(), PAGE_CHECK_EVERY_MS);
      stop.signal.addEventListener('abort', () => clearInterval(asking));
    }
    // The shell also asks when the window gets the focus back (AppComponent.onFocus).
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkForUpdates();
      }
    }, {signal: stop.signal});
  }

  /** Work to finish before the page reloads for a new version (it must not throw). */
  beforeReload(work: () => Promise<unknown>) {
    this.pendingWork.push(work);
  }

  /** Lets the unsaved work go out, for at most a few seconds, then loads the new version. */
  async reload() {
    const saved = Promise.all(this.pendingWork.map(work => work().catch(err => Logger.error('Saving before reload failed', err))));
    await Promise.race([saved, new Promise(resolve => setTimeout(resolve, SAVE_BEFORE_RELOAD_MS))]);
    this.reloadPage();
  }

  /** Apart, so a test can stand in for it. */
  protected reloadPage() {
    window.location.reload();
  }

  checkForUpdates(): Promise<unknown> | undefined {
    if (this.required()) {
      return;
    }
    if (this.updates.isEnabled) {
      Logger.log('Checking for updates...');
      return this.updates.checkForUpdate().catch(err => Logger.error('Checking for a new version failed', err));
    }
    if (this.loadedMain) {
      return this.checkServedPage();
    }
  }

  /** Without a service worker: compares the build this tab runs with the one the site serves now. */
  private async checkServedPage(): Promise<void> {
    if (this.pageChecking) {
      return;
    }
    this.pageChecking = true;
    try {
      const page = await this.fetchServedPage();
      const latest = page ? mainScriptOf(new DOMParser().parseFromString(page, 'text/html')) : '';
      if (latest && latest !== this.loadedMain && !this.required()) {
        Logger.log('A new version is out:', latest, 'this tab runs', this.loadedMain);
        this.required.set(true);
      }
    } catch (e) {
      // Offline, or the site did not answer: the next check asks again.
    } finally {
      this.pageChecking = false;
    }
  }

  /** The main script of the page this tab loaded. Apart, so a test can stand in for it. */
  protected mainScriptOfThisTab(): string {
    return mainScriptOf(document);
  }

  /** The site's index.html as it is now ('' when it does not answer). Apart, so a test can stand in for it. */
  protected async fetchServedPage(): Promise<string> {
    const response = await fetch('/index.html', {cache: 'no-store'});
    return response.ok ? response.text() : '';
  }
}

/** The page's hashed main script ('main-AB12CD34.js'); '' for the development server's plain main.js. */
export function mainScriptOf(doc: Document): string {
  for (const script of Array.from(doc.querySelectorAll('script[src]'))) {
    const match = /(?:^|\/)(main-[A-Za-z0-9]+\.js)$/.exec(script.getAttribute('src') || '');
    if (match) {
      return match[1];
    }
  }
  return '';
}
